/**
 * useEffectiveTemplate — single integration point for "template the
 * report viewer should render with".
 *
 * Default behaviour: returns `undefined` (no override). Each consumer
 * is free to derive its OWN active template — typically by reading
 * the Zustand store with `useStore(s => s.reportTemplates.find(...))`
 * — when no provider is mounted.
 *
 * Override behaviour: when an ancestor wraps the subtree in
 * `<TemplateOverrideProvider value={draftTemplate}>`, the hook returns
 * `draftTemplate` instead. This is how the Template Studio's preview
 * pane shows the user's UNSAVED draft template without polluting the
 * Zustand store. Symmetric with `ThemeOverrideProvider` in
 * `useEffectiveReportTheme.tsx` (SLK-T2 pattern).
 *
 * Architectural note (TSE-2 pass-2 C6):
 *
 *   `SiteRagReportView.tsx:730-745` resolves the active template from
 *   the Zustand store by `editedReport.templateId`. Threading a new
 *   `template` prop down through 30+ section components would be a
 *   wide diff. Instead, the viewer (and any nested sections that need
 *   the in-flight template) consult `useEffectiveTemplate()` FIRST,
 *   then fall back to the store-derived one. Production paths still
 *   render the persisted template — only the Studio preview sees the
 *   draft.
 *
 * Consumed by:
 *   - SiteRagReportView (override wins over the store lookup at
 *     line ~745 for the `activeTemplate` resolution).
 *   - Custom-section + cover-overlay renderers (TSE-2 C6).
 *   - Section-visibility check (TSE-2 C8) so the eye-toggle hides
 *     sections in the Studio preview live.
 */
import React from 'react';
import type { ReportTemplate } from '../types/reportTemplate';

const TemplateOverrideContext = React.createContext<ReportTemplate | null | undefined>(undefined);

/**
 * Wrap a subtree in this Provider to make every
 * `useEffectiveTemplate()` call inside it return the supplied
 * template instead of the store-derived one.
 *
 * `null` is a valid value — it means "force NO active template" for
 * tests that need to ensure the viewer falls through to its
 * archetype-seed defaults.
 */
export function TemplateOverrideProvider({
  value,
  children,
}: {
  value: ReportTemplate | null;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <TemplateOverrideContext.Provider value={value}>
      {children}
    </TemplateOverrideContext.Provider>
  );
}

/**
 * Resolve the template override (if any) for the current subtree.
 * Returns `undefined` outside a provider — callers should then derive
 * their own template, typically via the Zustand store.
 *
 * Returns `null` when the provider was mounted with an explicit
 * `null` value (i.e. "no template").
 */
export function useTemplateOverride(): ReportTemplate | null | undefined {
  return React.useContext(TemplateOverrideContext);
}
